import type { ButtonDesignTokens, ButtonTokenSections } from '@primeuix/themes/types/button';

export const root: ButtonTokenSections.Root = {
    // ===== ROOT BUTTON CONFIGURATION ===== (DS button — radius-medium, theme label weight, semantic focus ring)
    // Basic layout and spacing
    borderRadius: '{borderRadius.md}',
    roundedBorderRadius: '{extend.borderRadius.rounded}',
    gap: '{spacing.small.2}',
    paddingX: '{extend.buttonpaddinghorizontal}',
    paddingY: '{extend.buttonpaddingvertical}',
    iconOnlyWidth: '{extend.buttonheight}',

    // Size variants
    sm: {
        fontSize: '0.8125rem',
        paddingX: '{spacing.medium.2}',
        paddingY: '{spacing.small.3}',
        iconOnlyWidth: '{extend.buttonheight}',
    },
    lg: {
        fontSize: '1.0625rem',
        paddingX: '{spacing.medium.3}',
        paddingY: '{spacing.medium.1}',
        iconOnlyWidth: '{extend.buttonheightlarge}',
    },

    // Typography
    label: {
        fontWeight: '{extend.buttonfontweight}',
    },

    // Effects and interaction
    raisedShadow: '0 3px 1px -2px #0003, 0 2px 2px #00000024, 0 1px 5px #0000001f',
    transitionDuration: '{extend.transitionDuration.fast}',

    // Focus ring: the theme's single semantic rule (DS: 2px wine, 2px offset)
    focusRing: {
        width: '{focusRing.width}',
        style: '{focusRing.style}',
        offset: '{focusRing.offset}',
    },

    // Badge styling for notification badges on buttons
    badgeSize: '1rem',
};

export const colorScheme: ButtonTokenSections.ColorScheme = {
    light: {
        root: {
            // ===== PRIMARY BUTTON (CLEVERLY BRAND ORANGE) =====
            primary: {
                background: '{primary.500}',
                hoverBackground: '{primary.600}',
                activeBackground: '{primary.700}',
                borderColor: '{primary.500}',
                hoverBorderColor: '{primary.600}',
                activeBorderColor: '{primary.700}',
                color: '#FFFFFF',
                hoverColor: '#FFFFFF',
                activeColor: '#FFFFFF',
                focusRing: {
                    color: '{focusRing.color}',
                    shadow: 'none',
                },
            },

            // ===== SECONDARY BUTTON (brand guide, O14: transparent, 1.5px neutral border, neutral-darkest label) =====
            // Theme-owned via extend tokens so the legacy preset keeps its filled ink look.
            secondary: {
                background: '{extend.buttonsecondarybackground}',
                hoverBackground: '{extend.buttonsecondaryhoverbackground}',
                activeBackground: '{extend.buttonsecondaryactivebackground}',
                borderColor: '{extend.buttonsecondarybordercolor}',
                hoverBorderColor: '{extend.buttonsecondaryhoverbordercolor}',
                activeBorderColor: '{extend.buttonsecondaryactivebordercolor}',
                color: '{extend.buttonsecondarycolor}',
                hoverColor: '{extend.buttonsecondarycolor}',
                activeColor: '{extend.buttonsecondarycolor}',
                focusRing: {
                    color: '{focusRing.color}',
                    shadow: 'none',
                },
            },

            // ===== SUCCESS BUTTON (GREEN) =====
            success: {
                background: '{green.500}',
                hoverBackground: '{green.600}',
                activeBackground: '{green.700}',
                borderColor: '{green.500}',
                hoverBorderColor: '{green.600}',
                activeBorderColor: '{green.700}',
                color: '#FFFFFF',
                hoverColor: '#FFFFFF',
                activeColor: '#FFFFFF',
                focusRing: {
                    color: '{focusRing.color}',
                    shadow: 'none',
                },
            },

            // ===== INFO BUTTON (DS BLUE) =====
            info: {
                background: '{blue.600}',
                hoverBackground: '{blue.700}',
                activeBackground: '{blue.800}',
                borderColor: '{blue.600}',
                hoverBorderColor: '{blue.700}',
                activeBorderColor: '{blue.800}',
                color: '#FFFFFF',
                hoverColor: '#FFFFFF',
                activeColor: '#FFFFFF',
                focusRing: {
                    color: '{focusRing.color}',
                    shadow: 'none',
                },
            },

            // ===== WARN BUTTON (YELLOW, DARK LABEL) =====
            warn: {
                background: '{yellow.400}',
                hoverBackground: '{yellow.500}',
                activeBackground: '{yellow.600}',
                borderColor: '{yellow.400}',
                hoverBorderColor: '{yellow.500}',
                activeBorderColor: '{yellow.600}',
                color: '{yellow.900}',
                hoverColor: '{yellow.900}',
                activeColor: '{yellow.950}',
                focusRing: {
                    color: '{focusRing.color}',
                    shadow: 'none',
                },
            },

            // ===== HELP BUTTON (PURPLE) =====
            help: {
                background: '{purple.500}',
                hoverBackground: '{purple.600}',
                activeBackground: '{purple.700}',
                borderColor: '{purple.500}',
                hoverBorderColor: '{purple.600}',
                activeBorderColor: '{purple.700}',
                color: '#FFFFFF',
                hoverColor: '#FFFFFF',
                activeColor: '#FFFFFF',
                focusRing: {
                    color: '{focusRing.color}',
                    shadow: 'none',
                },
            },

            // ===== DANGER BUTTON (WINE) =====
            danger: {
                background: '{wine.900}',
                hoverBackground: '{wine.800}',
                activeBackground: '{wine.950}',
                borderColor: '{wine.900}',
                hoverBorderColor: '{wine.800}',
                activeBorderColor: '{wine.950}',
                color: '#FFFFFF',
                hoverColor: '#FFFFFF',
                activeColor: '#FFFFFF',
                focusRing: {
                    color: '{focusRing.color}',
                    shadow: 'none',
                },
            },

            // ===== CONTRAST BUTTON (INK) =====
            contrast: {
                background: '{sand.950}',
                hoverBackground: '{grey.800}',
                activeBackground: '{grey.700}',
                borderColor: '{sand.950}',
                hoverBorderColor: '{grey.800}',
                activeBorderColor: '{grey.700}',
                color: '#FFFFFF',
                hoverColor: '#FFFFFF',
                activeColor: '#FFFFFF',
                focusRing: {
                    color: '{focusRing.color}',
                    shadow: 'none',
                },
            },
        },

        // ===== OUTLINED BUTTON VARIANTS =====
        outlined: {
            primary: {
                hoverBackground: '{primary.50}',
                activeBackground: '{primary.100}',
                borderColor: '{grey.200}',
                color: '{sand.950}',
            },
            secondary: {
                hoverBackground: '{grey.50}',
                activeBackground: '{grey.100}',
                borderColor: '{grey.200}',
                color: '{sand.950}',
            },
            success: {
                hoverBackground: 'color-mix(in srgb, {green.500}, transparent 90%)',
                activeBackground: 'color-mix(in srgb, {green.500}, transparent 80%)',
                borderColor: '{green.500}',
                color: '{green.500}',
            },
            info: {
                hoverBackground: 'color-mix(in srgb, {primary.500}, transparent 90%)',
                activeBackground: 'color-mix(in srgb, {primary.500}, transparent 80%)',
                borderColor: '{primary.500}',
                color: '{primary.500}',
            },
            warn: {
                hoverBackground: 'color-mix(in srgb, {yellow.500}, transparent 90%)',
                activeBackground: 'color-mix(in srgb, {yellow.500}, transparent 80%)',
                borderColor: '{yellow.500}',
                color: '{yellow.600}',
            },
            help: {
                hoverBackground: 'color-mix(in srgb, {purple.500}, transparent 90%)',
                activeBackground: 'color-mix(in srgb, {purple.500}, transparent 80%)',
                borderColor: '{purple.500}',
                color: '{purple.500}',
            },
            danger: {
                hoverBackground: 'color-mix(in srgb, {red.500}, transparent 90%)',
                activeBackground: 'color-mix(in srgb, {red.500}, transparent 80%)',
                borderColor: '{red.500}',
                color: '{red.500}',
            },
            contrast: {
                hoverBackground: '{grey.50}',
                activeBackground: '{grey.100}',
                borderColor: '{sand.950}',
                color: '{sand.950}',
            },
            plain: {
                hoverBackground: '{surface.100}',
                activeBackground: '{surface.200}',
                borderColor: '{surface.500}',
                color: '{surface.700}',
            },
        },

        // ===== TEXT BUTTON VARIANTS =====
        text: {
            primary: {
                hoverBackground: '{primary.50}',
                activeBackground: '{primary.100}',
                color: '{primary.600}',
            },
            secondary: {
                hoverBackground: '{grey.50}',
                activeBackground: '{grey.100}',
                color: '{grey.600}',
            },
            success: {
                hoverBackground: 'color-mix(in srgb, {green.500}, transparent 90%)',
                activeBackground: 'color-mix(in srgb, {green.500}, transparent 80%)',
                color: '{green.500}',
            },
            info: {
                hoverBackground: 'color-mix(in srgb, {primary.500}, transparent 90%)',
                activeBackground: 'color-mix(in srgb, {primary.500}, transparent 80%)',
                color: '{primary.500}',
            },
            warn: {
                hoverBackground: 'color-mix(in srgb, {yellow.500}, transparent 90%)',
                activeBackground: 'color-mix(in srgb, {yellow.500}, transparent 80%)',
                color: '{yellow.600}',
            },
            help: {
                hoverBackground: 'color-mix(in srgb, {purple.500}, transparent 90%)',
                activeBackground: 'color-mix(in srgb, {purple.500}, transparent 80%)',
                color: '{purple.500}',
            },
            danger: {
                hoverBackground: 'color-mix(in srgb, {red.500}, transparent 90%)',
                activeBackground: 'color-mix(in srgb, {red.500}, transparent 80%)',
                color: '{red.500}',
            },
            contrast: {
                hoverBackground: 'color-mix(in srgb, {surface.900}, transparent 90%)',
                activeBackground: 'color-mix(in srgb, {surface.900}, transparent 80%)',
                color: '{surface.900}',
            },
            plain: {
                hoverBackground: '{surface.100}',
                activeBackground: '{surface.200}',
                color: '{surface.700}',
            },
        },

        // ===== LINK BUTTON VARIANT =====
        link: {
            color: '{extend.buttonlinkcolor}',
            hoverColor: '{extend.buttonlinkhovercolor}',
            activeColor: '{extend.buttonlinkhovercolor}',
        },
    },
    dark: {
        root: {
            // ===== PRIMARY BUTTON (SAME AS LIGHT) =====
            primary: {
                background: '{primary.500}',
                hoverBackground: '{primary.400}',
                activeBackground: '{primary.600}',
                borderColor: '{primary.500}',
                hoverBorderColor: '{primary.400}',
                activeBorderColor: '{primary.600}',
                color: '{surface.50}',
                hoverColor: '{surface.50}',
                activeColor: '{surface.50}',
                focusRing: {
                    color: '{primary.500}',
                    shadow: '0 0 0 2px color-mix(in srgb, {primary.500}, transparent 70%)',
                },
            },

            // ===== SECONDARY BUTTON (SAME AS LIGHT) =====
            secondary: {
                background: '{surface.800}',
                hoverBackground: '{surface.700}',
                activeBackground: '{surface.900}',
                borderColor: '{surface.600}',
                hoverBorderColor: '{surface.500}',
                activeBorderColor: '{surface.700}',
                color: '{surface.200}',
                hoverColor: '{surface.100}',
                activeColor: '{surface.200}',
                focusRing: {
                    color: '{surface.400}',
                    shadow: '0 0 0 2px color-mix(in srgb, {surface.400}, transparent 70%)',
                },
            },

            // ===== SUCCESS BUTTON (GREEN) =====
            success: {
                background: '{green.500}',
                hoverBackground: '{green.400}',
                activeBackground: '{green.600}',
                borderColor: '{green.500}',
                hoverBorderColor: '{green.400}',
                activeBorderColor: '{green.600}',
                color: '{surface.50}',
                hoverColor: '{surface.50}',
                activeColor: '{surface.50}',
                focusRing: {
                    color: '{green.500}',
                    shadow: '0 0 0 2px color-mix(in srgb, {green.500}, transparent 70%)',
                },
            },

            // ===== INFO BUTTON (BLUE - SAME AS PRIMARY) =====
            info: {
                background: '{primary.500}',
                hoverBackground: '{primary.400}',
                activeBackground: '{primary.600}',
                borderColor: '{primary.500}',
                hoverBorderColor: '{primary.400}',
                activeBorderColor: '{primary.600}',
                color: '{surface.50}',
                hoverColor: '{surface.50}',
                activeColor: '{surface.50}',
                focusRing: {
                    color: '{primary.500}',
                    shadow: '0 0 0 2px color-mix(in srgb, {primary.500}, transparent 70%)',
                },
            },

            // ===== WARN BUTTON (YELLOW/ORANGE) =====
            warn: {
                background: '{yellow.500}',
                hoverBackground: '{yellow.400}',
                activeBackground: '{yellow.600}',
                borderColor: '{yellow.500}',
                hoverBorderColor: '{yellow.400}',
                activeBorderColor: '{yellow.600}',
                color: '{surface.900}',
                hoverColor: '{surface.900}',
                activeColor: '{surface.900}',
                focusRing: {
                    color: '{yellow.500}',
                    shadow: '0 0 0 2px color-mix(in srgb, {yellow.500}, transparent 70%)',
                },
            },

            // ===== HELP BUTTON (PURPLE) =====
            help: {
                background: '{purple.500}',
                hoverBackground: '{purple.400}',
                activeBackground: '{purple.600}',
                borderColor: '{purple.500}',
                hoverBorderColor: '{purple.400}',
                activeBorderColor: '{purple.600}',
                color: '{surface.50}',
                hoverColor: '{surface.50}',
                activeColor: '{surface.50}',
                focusRing: {
                    color: '{purple.500}',
                    shadow: '0 0 0 2px color-mix(in srgb, {purple.500}, transparent 70%)',
                },
            },

            // ===== DANGER BUTTON (RED) =====
            danger: {
                background: '{red.500}',
                hoverBackground: '{red.400}',
                activeBackground: '{red.600}',
                borderColor: '{red.500}',
                hoverBorderColor: '{red.400}',
                activeBorderColor: '{red.600}',
                color: '{surface.50}',
                hoverColor: '{surface.50}',
                activeColor: '{surface.50}',
                focusRing: {
                    color: '{red.500}',
                    shadow: '0 0 0 2px color-mix(in srgb, {red.500}, transparent 70%)',
                },
            },

            // ===== CONTRAST BUTTON (LIGHT) =====
            contrast: {
                background: '{surface.100}',
                hoverBackground: '{surface.200}',
                activeBackground: '{surface.50}',
                borderColor: '{surface.100}',
                hoverBorderColor: '{surface.200}',
                activeBorderColor: '{surface.50}',
                color: '{surface.900}',
                hoverColor: '{surface.900}',
                activeColor: '{surface.900}',
                focusRing: {
                    color: '{surface.100}',
                    shadow: '0 0 0 2px color-mix(in srgb, {surface.100}, transparent 70%)',
                },
            },
        },
        outlined: {
            primary: {
                hoverBackground: 'color-mix(in srgb, {primary.500}, transparent 90%)',
                activeBackground: 'color-mix(in srgb, {primary.500}, transparent 80%)',
                borderColor: '{primary.500}',
                color: '{primary.400}',
            },
            secondary: {
                hoverBackground: 'color-mix(in srgb, {surface.400}, transparent 90%)',
                activeBackground: 'color-mix(in srgb, {surface.400}, transparent 80%)',
                borderColor: '{surface.600}',
                color: '{surface.300}',
            },
            success: {
                hoverBackground: 'color-mix(in srgb, {green.500}, transparent 90%)',
                activeBackground: 'color-mix(in srgb, {green.500}, transparent 80%)',
                borderColor: '{green.500}',
                color: '{green.400}',
            },
            info: {
                hoverBackground: 'color-mix(in srgb, {primary.500}, transparent 90%)',
                activeBackground: 'color-mix(in srgb, {primary.500}, transparent 80%)',
                borderColor: '{primary.500}',
                color: '{primary.400}',
            },
            warn: {
                hoverBackground: 'color-mix(in srgb, {yellow.500}, transparent 90%)',
                activeBackground: 'color-mix(in srgb, {yellow.500}, transparent 80%)',
                borderColor: '{yellow.500}',
                color: '{yellow.400}',
            },
            help: {
                hoverBackground: 'color-mix(in srgb, {purple.500}, transparent 90%)',
                activeBackground: 'color-mix(in srgb, {purple.500}, transparent 80%)',
                borderColor: '{purple.500}',
                color: '{purple.400}',
            },
            danger: {
                hoverBackground: 'color-mix(in srgb, {red.500}, transparent 90%)',
                activeBackground: 'color-mix(in srgb, {red.500}, transparent 80%)',
                borderColor: '{red.500}',
                color: '{red.400}',
            },
            contrast: {
                hoverBackground: 'color-mix(in srgb, {surface.100}, transparent 90%)',
                activeBackground: 'color-mix(in srgb, {surface.100}, transparent 80%)',
                borderColor: '{surface.100}',
                color: '{surface.200}',
            },
            plain: {
                hoverBackground: '{surface.800}',
                activeBackground: '{surface.700}',
                borderColor: '{surface.600}',
                color: '{surface.300}',
            },
        },
        text: {
            primary: {
                hoverBackground: 'color-mix(in srgb, {primary.500}, transparent 90%)',
                activeBackground: 'color-mix(in srgb, {primary.500}, transparent 80%)',
                color: '{primary.400}',
            },
            secondary: {
                hoverBackground: 'color-mix(in srgb, {surface.400}, transparent 90%)',
                activeBackground: 'color-mix(in srgb, {surface.400}, transparent 80%)',
                color: '{surface.300}',
            },
            success: {
                hoverBackground: 'color-mix(in srgb, {green.500}, transparent 90%)',
                activeBackground: 'color-mix(in srgb, {green.500}, transparent 80%)',
                color: '{green.400}',
            },
            info: {
                hoverBackground: 'color-mix(in srgb, {primary.500}, transparent 90%)',
                activeBackground: 'color-mix(in srgb, {primary.500}, transparent 80%)',
                color: '{primary.400}',
            },
            warn: {
                hoverBackground: 'color-mix(in srgb, {yellow.500}, transparent 90%)',
                activeBackground: 'color-mix(in srgb, {yellow.500}, transparent 80%)',
                color: '{yellow.400}',
            },
            help: {
                hoverBackground: 'color-mix(in srgb, {purple.500}, transparent 90%)',
                activeBackground: 'color-mix(in srgb, {purple.500}, transparent 80%)',
                color: '{purple.400}',
            },
            danger: {
                hoverBackground: 'color-mix(in srgb, {red.500}, transparent 90%)',
                activeBackground: 'color-mix(in srgb, {red.500}, transparent 80%)',
                color: '{red.400}',
            },
            contrast: {
                hoverBackground: 'color-mix(in srgb, {surface.100}, transparent 90%)',
                activeBackground: 'color-mix(in srgb, {surface.100}, transparent 80%)',
                color: '{surface.200}',
            },
            plain: {
                hoverBackground: '{surface.800}',
                activeBackground: '{surface.700}',
                color: '{surface.300}',
            },
        },
        link: {
            color: '{primary.400}',
            hoverColor: '{primary.300}',
            activeColor: '{primary.500}',
        },
    },
};

export default {
    root,
    colorScheme,
} satisfies ButtonDesignTokens;
